'use client'

import { MainLandingScreen } from '@/components/landing/main-landing-screen'
import GtmTracker from '@/components/gtm/gtm-tracker'
import FunnelLatestStepTracker from '@/components/posthog/funnel-latest-step-tracker'
import UtmTracker from '@/components/utm/utm-tracker'
import { getMainLandingTrackingProps } from '@/lib/main-landing'
import { getFunnelLatestQuizProperties } from '@/lib/posthog-funnel-latest'
import { useRouter } from 'next/navigation'
import { useEffect, useMemo } from 'react'

/**
 * The landing screen, shared by `/[locale]` and `/[locale]/latest`.
 *
 * These two routes were byte-identical 48-line files. Ads land on both, so both
 * have to keep working — but the tracker mounts here are the funnel's
 * attribution contract (see ANALYTICS.md), and two copies is two places for
 * them to drift.
 */
export function LandingRoute() {
  const router = useRouter()

  // Warm the next step while the visitor reads the first question.
  //
  // The quiz advances through an in-page `switch`, so `/latest/new` is the
  // first *real* navigation in the funnel and the only one on this screen. It
  // cannot be a `<Link>`: the CTA carries the visitor's answer, which has to be
  // written to sessionStorage before the route changes. Prefetching gets the
  // latency win without touching that path — by the time they tap, the
  // destination's shell is already on the client.
  useEffect(() => {
    router.prefetch('/latest/new')
  }, [router])

  const posthogProperties = useMemo(
    () => ({
      ...getFunnelLatestQuizProperties('gender'),
      ...getMainLandingTrackingProps(),
    }),
    [],
  )

  const gtmBase = useMemo(
    () => ({
      product: 'roastdating' as const,
      variant: 'latest' as const,
      ...getMainLandingTrackingProps(),
    }),
    [],
  )

  return (
    <>
      <UtmTracker />
      <FunnelLatestStepTracker stepKey="gender" />
      <GtmTracker event="view_item" data={gtmBase} />
      <GtmTracker
        event="funnel_progress"
        data={{
          ...gtmBase,
          step_number: 1,
          step_name: 'gender',
        }}
      />

      <MainLandingScreen
        posthogProperties={posthogProperties}
        onNext={() => router.push('/latest/new')}
      />
    </>
  )
}

export default LandingRoute
